import React, { useMemo } from "react";

export interface DateRangePickerProps {
  /** Start date in ISO 8601 format (YYYY-MM-DD) */
  startDate?: string;
  /** End date in ISO 8601 format (YYYY-MM-DD) */
  endDate?: string;
  /** Callback when dates change */
  onChange: (startDate: string, endDate: string) => void;
  /** Whether the inputs are disabled */
  disabled?: boolean;
  /** Whether both dates are required */
  required?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Error messages to display */
  errors?: {
    startDate?: string;
    endDate?: string;
    dateRange?: string;
  };
}

/**
 * Validates that a date string is in valid ISO 8601 format (YYYY-MM-DD).
 */
const isValidISODate = (dateStr: string | undefined): boolean => {
  if (!dateStr) return false;
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateStr)) return false;
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
};

/**
 * Formats a Date object to ISO 8601 string (YYYY-MM-DD).
 */
const toISODateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Date Range Picker component.
 *
 * Provides two date inputs for selecting a start and end date range.
 * Validates that both dates are provided and that start date is not after end date.
 *
 * **Validates: Requirements 1.1, 1.2, 1.3, 1.4**
 */
const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  disabled = false,
  required = false,
  className = "",
  errors = {},
}) => {
  const hasError = useMemo(() => {
    return (
      errors.startDate ||
      errors.endDate ||
      errors.dateRange ||
      (required && !startDate) ||
      (required && !endDate)
    );
  }, [errors, required, startDate, endDate]);

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    onChange(newStartDate, endDate || "");
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value;
    onChange(startDate || "", newEndDate);
  };

  // Calculate max date (today + 5 years) for date input constraints
  const maxDate = useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 5);
    return toISODateString(date);
  }, []);

  // Calculate min date (10 years ago) for date input constraints
  const minDate = useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 10);
    return toISODateString(date);
  }, []);

  return (
    <div className={className}>
      <div className="grid grid-cols-2 gap-4">
        {/* Start Date Input */}
        <div>
          <label
            htmlFor="start-date"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Start Date
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type="date"
            id="start-date"
            value={startDate || ""}
            onChange={handleStartDateChange}
            disabled={disabled}
            min={minDate}
            max={maxDate}
            className={[
              "block w-full rounded-md border-gray-300 shadow-sm",
              "focus:border-primary-500 focus:ring-primary-500 sm:text-sm",
              "disabled:bg-gray-100 disabled:cursor-not-allowed",
              errors.startDate || errors.dateRange
                ? "border-red-300"
                : "border-gray-300",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-describedby={
              errors.startDate || errors.dateRange
                ? "start-date-error"
                : undefined
            }
          />
          {errors.startDate && (
            <p
              id="start-date-error"
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {errors.startDate}
            </p>
          )}
        </div>

        {/* End Date Input */}
        <div>
          <label
            htmlFor="end-date"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            End Date
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type="date"
            id="end-date"
            value={endDate || ""}
            onChange={handleEndDateChange}
            disabled={disabled}
            min={minDate}
            max={maxDate}
            className={[
              "block w-full rounded-md border-gray-300 shadow-sm",
              "focus:border-primary-500 focus:ring-primary-500 sm:text-sm",
              "disabled:bg-gray-100 disabled:cursor-not-allowed",
              errors.endDate || errors.dateRange
                ? "border-red-300"
                : "border-gray-300",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-describedby={
              errors.endDate || errors.dateRange ? "end-date-error" : undefined
            }
          />
          {errors.endDate && (
            <p
              id="end-date-error"
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {errors.endDate}
            </p>
          )}
        </div>
      </div>

      {/* Date Range Error */}
      {errors.dateRange && (
        <p
          id="date-range-error"
          className="mt-2 text-sm text-red-600"
          role="alert"
        >
          {errors.dateRange}
        </p>
      )}

      {/* Validation summary for required fields */}
      {required && !hasError && (startDate || endDate) && (
        <p className="mt-2 text-xs text-gray-500">
          {startDate && endDate
            ? `${startDate} to ${endDate}`
            : "Please complete both dates"}
        </p>
      )}
    </div>
  );
};

export default DateRangePicker;
